/**
 * Per-merchant PayPal REST API credentials (Client ID + Secret), used for a
 * real PayPal Orders API integration that can verify a payment actually
 * completed — unlike the off-platform paypal.me/button links, which BookAPT
 * has no way to verify.
 *
 * The secret is never sent back to any client after saving — only the
 * backend (Admin SDK) can read it, from a Firestore collection that's
 * explicitly locked down in firestore.rules. See backend/server.js's
 * /api/business/:businessId/paypal-api-credentials routes.
 */

import { Platform } from 'react-native';
import { WIDGET_BOOKING_API } from '../utils/widgetEmbed';

const API_BASE_URL = __DEV__ ? 'http://localhost:3000/api' : WIDGET_BOOKING_API;

async function getAuthHeader(): Promise<Record<string, string>> {
  try {
    if (Platform.OS === 'web') {
      const { getWebAuth } = require('./unifiedAuthService');
      const auth = getWebAuth();
      const token = await auth?.currentUser?.getIdToken?.();
      return token ? { Authorization: `Bearer ${token}` } : {};
    }
    const { getApp } = require('@react-native-firebase/app');
    const { getAuth } = require('@react-native-firebase/auth');
    const token = await getAuth(getApp())?.currentUser?.getIdToken?.();
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

export interface PaypalApiCredentialsStatus {
  configured: boolean;
  clientIdPreview?: string;
  mode?: 'live' | 'sandbox';
}

export async function savePaypalApiCredentials(
  businessId: string,
  clientId: string,
  clientSecret: string,
  mode: 'live' | 'sandbox' = 'live'
): Promise<{ success: boolean; error?: string }> {
  try {
    const headers = await getAuthHeader();
    const response = await fetch(
      `${API_BASE_URL}/business/${encodeURIComponent(businessId)}/paypal-api-credentials`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({ clientId, clientSecret, mode }),
      }
    );
    const result = await response.json();
    return result;
  } catch (error: any) {
    return { success: false, error: error?.message || 'Could not save credentials.' };
  }
}

export async function getPaypalApiCredentialsStatus(
  businessId: string
): Promise<PaypalApiCredentialsStatus> {
  try {
    const headers = await getAuthHeader();
    const response = await fetch(
      `${API_BASE_URL}/business/${encodeURIComponent(businessId)}/paypal-api-credentials/status`,
      { headers }
    );
    const result = await response.json();
    if (!result.success) return { configured: false };
    return {
      configured: !!result.configured,
      clientIdPreview: result.clientIdPreview,
      mode: result.mode,
    };
  } catch {
    return { configured: false };
  }
}

export async function removePaypalApiCredentials(
  businessId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const headers = await getAuthHeader();
    const response = await fetch(
      `${API_BASE_URL}/business/${encodeURIComponent(businessId)}/paypal-api-credentials`,
      { method: 'DELETE', headers }
    );
    return await response.json();
  } catch (error: any) {
    return { success: false, error: error?.message || 'Could not remove credentials.' };
  }
}
